/**
 * Maps `protos.vision.assets.v1` wire types to the artboard image shape consumed by
 * `projectStore` and the artboard components, so the UI is unaware of the transport.
 * The proto `ProjectImage` carries server-presigned view URLs, which map onto
 * `original_path` / `thumbnail_path` (the artboard's URL resolver passes a full https
 * URL through unchanged).
 */

import { timestampDate } from '@bufbuild/protobuf/wkt';

import { cropToLegacy } from './visionGenerationsMapping.js';
import { regionsToLegacy } from './visionMaskRegionsMapping.js';

function emptyToNull(value) {
  return value ? value : null;
}

/** A proto `ProjectImage` -> the legacy artboard image record. */
export function projectImageToLegacy(image) {
  return {
    id: image.assetId,
    project_id: image.projectId,
    category_id: emptyToNull(image.locationId),
    original_path: image.originalUrl,
    thumbnail_path: image.thumbnailUrl,
    width: image.width,
    height: image.height,
    file_size: Number(image.storageSizeBytes ?? 0),
    sort_order: image.sortOrder,
    frame_number: image.frameNumber || '',
    comment: image.comment || '',
    description: image.description || '',
    display_user_locked: image.displayUserLocked,
    selected_generation_id: emptyToNull(image.selectedGenerationId),
    generated_thumbnail_path: emptyToNull(image.generatedThumbnailUrl),
    graded_thumbnail_path: emptyToNull(image.gradedThumbnailUrl),
    crop_params: cropToLegacy(image.crop),
    mask_regions: regionsToLegacy(image.maskRegions),
    has_generation: Boolean(image.selectedGenerationId),
    uploaded_by: image.uploadedByAccountId,
    uploaded_at: image.createdAt ? timestampDate(image.createdAt).toISOString() : null,
    updated_at: image.updatedAt ? timestampDate(image.updatedAt).toISOString() : null,
  };
}

/** A `ListProjectImagesResponse` -> `{ images, nextPageToken }` in legacy shape. */
export function listResponseToImages(response) {
  const images = (response.result?.images ?? []).map(projectImageToLegacy);
  const nextPageToken = response.pagination?.nextPageToken || null;

  return { images, nextPageToken };
}

export function referenceImageToLegacy(reference) {
  return {
    id: reference.assetId,
    project_id: reference.projectId,
    image_path: reference.originalUrl,
    thumbnail_path: reference.thumbnailUrl,
    uploaded_by: reference.uploadedByAccountId,
  };
}

export function colorPresetToLegacy(preset) {
  return {
    id: preset.presetId,
    name: preset.name,
    image_path: preset.originalUrl,
    thumbnail_path: preset.thumbnailUrl,
    counts_toward_storage: preset.countsTowardStorage,
    isDefault: false,
  };
}
